import _ from "lib/underscore";

/**
 * Is a given variable defined and has value?
 * @param {*} val
 * @return {boolean} <code>false</code> if given value is <code>undefined</code> or <code>null</code>, otherwise <code>true</code>
 */
export function isset(val) {
	return !_.isUndefined(val) && !_.isNull(val);
}

/**
 * Returns object's property or array's index if exists and default value otherwise
 * @param {object|array} object Inspected object or array
 * @param {string|number} key Property name for objects, numeric index for arrays
 * @param {*} defaultValue
 * @return {*}
 */
export function element(object, key, defaultValue = null) {
	return _.has(object, key) ? object[key] : defaultValue;
}

/**
 * Returns object's property or array's index if exists and is not <code>null</code> or default value otherwise
 * @param {object|array} object Inspected object or array
 * @param {string|number} key Property name for objects, numeric index for arrays
 * @param {*} defaultValue
 * @return {*}
 */
export function elementOrDefault(object, key, defaultValue = null) {
	return _.has(object, key) && object[key] !== null ? object[key] : defaultValue;
}

/**
 * Returns object's property or array's index if exists and is not <code>null</code> or "&mdash;" value otherwise
 * @param {object|array} object Inspected object or array
 * @param {string|number} key Property name for objects, numeric index for arrays
 * @param {*} defaultValue
 * @return {*}
 */
export function elementOrDash(object, key) {
	return elementOrDefault(object, key, '&mdash;');
}

/**
 * Returns value if it's not <code>undefined</code> or <code>null</code>, otherwise defaultValue
 * @param {*} value
 * @param {*} defaultValue
 * @return {*}
 */
export function orDefault(value, defaultValue = null) {
	return isset(value) ? value : defaultValue;
}

/**
 * Converts given value to number using standard javascript converter: <code>+value</code>
 * @param {*} value Input value
 * @param {number} defaultValue
 * @return {number} Either converted value if it's not <code>NaN</code> and default value otherwise
 */
export function asNumber(value, defaultValue = 0) {
	value = +value;
	return !isNaN(value) ? value : 0;
}

/**
 * Converts given value to boolean using such algo:
 * - string <code>"0"</code> results in <code>false</code>;
 * - string <code>"false"</code> results in <code>false</code>;
 * - string <code>"null"</code> results in <code>false</code>;
 * - empty object results in <code>false</code>;
 * - empty array results in <code>false</code>;
 * - other values go through standard javascript converter: <code>!!value</code>
 * @param {*} value Input value
 * @return {boolean} Converted value
 */
export function asBoolean(value) {
	if (_.isBoolean(value)) return value;
	if (value === '0') return false;
	if (value === 'false') return false;
	if (value === 'null') return false;
	if (_.isObject(value) && _.isEmpty(value)) return false;
	if (_.isArray(value) && _.isEmpty(value)) return false;
	return !!value;
}

/**
 * Converts given value to string using standard javascript converter: <code>''+value</code>
 * except that <code>null</code> and <code>undefined</code> values will be converted to empty string.
 * @param {*} value Input value
 * @return {string} Converted value
 */
export function asString(value) {
	if (value === null || _.isUndefined(value)) return '';
	return '' + value;
}

/**
 * Converts given value to string using {@link asString()} and trims whitespace characters.
 * @param {string|*} value
 * @return {string} Converted and trimmed value
 */
export function trim(value) {
	return asString(value).trim();
}

/**
 * If input value is an array - it will be returned as is.
 * In other cases input value will be converted to string and either split with given delimiter
 * or just returned as a single array element.
 * @param {array|string} value
 * @param {string} delimiter
 * @return {array}
 */
export function asArray(value, delimiter = '') {
	if (!value) return [];
	if (_.isArray(value)) return value;
	value = asString(value);
	return delimiter ? value.split(delimiter) : [value];
}


// WEBPACK FOOTER //
// ./core/utils/var.js